import React, { useMemo } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Divider,
    Typography,
    useMediaQuery,
} from '@mui/material';
import moment from 'moment';
import { theme } from '../../../../../shared/theme';
import SpaceItinerary from '../../Spaces/SpaceItinerary';

type Props = {
    reservation: any;
    expanded: boolean;
    onToggle: (event: React.SyntheticEvent, isExpanded: boolean) => void;
};

export default function ReservationItinerarySection({
    reservation,
    expanded,
    onToggle,
}: Props) {
    const isMobile = useMediaQuery('(max-width: 770px)');

    const outerAccordionSx = {
        mt: 2,
        borderRadius: 2,
        boxShadow: 'none',
        '&::before': { display: 'none' },
        overflow: 'hidden',
    };

    const cardSx = {
        borderRadius: 2,
        py: 1.5,
        mb: 2,
    };

    const labelSx = {
        fontWeight: 800,
        color: theme.palette.text.secondary,
        lineHeight: 1.35,
    };

    const valueSx = {
        color: theme.palette.text.primary,
        lineHeight: 1.35,
        wordBreak: 'break-word' as const,
    };

    const gridSx = {
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'minmax(150px, 210px) 1fr',
        columnGap: isMobile ? 0 : 3,
        rowGap: 1.25,
        width: '100%',
    };

    const bi = reservation?.bookingInfo;

    const isCommercial = useMemo(() => {
        const t = String(bi?.moduleType || reservation?.module?.type || '')
            .toLowerCase()
            .trim();
        return t === 'commercial';
    }, [bi?.moduleType, reservation?.module?.type]);

    const spaces = useMemo(() => {
        const s =
            reservation?.spaces ??
            reservation?.module?.spaces ??
            reservation?.asset?.spaces;
        return Array.isArray(s) ? s : [];
    }, [reservation]);

    const showSpaceItinerary = !isCommercial && spaces.length > 0;

    const commercialItems = useMemo(() => {
        if (!isCommercial) return [];

        const entireVenue = Boolean(bi?.entireVenue);
        const desks = Number(bi?.desks || 0);
        const meetingRooms = Number(bi?.meetingRooms || 0);
        const privateOffices = Number(bi?.privateOffices || 0);

        const items: { label: string; value: string }[] = [];

        if (entireVenue) {
            items.push({ label: 'Booking', value: 'Entire venue' });
        } else {
            if (desks > 0) items.push({ label: 'Desks', value: String(desks) });
            if (meetingRooms > 0)
                items.push({
                    label: 'Meeting rooms',
                    value: String(meetingRooms),
                });
            if (privateOffices > 0)
                items.push({
                    label: 'Private offices',
                    value: String(privateOffices),
                });
            if (desks === 0 && meetingRooms === 0 && privateOffices === 0)
                items.push({ label: 'Booking', value: '—' });
        }

        const guests = Number(bi?.guests || 0);
        if (!Number.isNaN(guests) && guests > 0) {
            items.push({ label: 'Guests', value: String(guests) });
        }

        const checkinTime = String(bi?.checkinTime || '').trim();
        const checkoutTime = String(bi?.checkoutTime || '').trim();
        const timeText =
            checkinTime && checkoutTime
                ? `${checkinTime} → ${checkoutTime}`
                : '—';
        items.push({ label: 'Time', value: timeText });

        const start = reservation?.startDate
            ? moment(reservation.startDate)
            : null;
        const end = reservation?.endDate ? moment(reservation.endDate) : null;

        const dateText =
            start?.isValid() && end?.isValid()
                ? start.isSame(end, 'day')
                    ? start.format('ddd D MMM YYYY')
                    : `${start.format('ddd D MMM YYYY')} → ${end.format(
                          'ddd D MMM YYYY',
                      )}`
                : '—';

        items.push({ label: 'Date', value: dateText });

        return items;
    }, [
        isCommercial,
        bi?.entireVenue,
        bi?.desks,
        bi?.meetingRooms,
        bi?.privateOffices,
        bi?.guests,
        bi?.checkinTime,
        bi?.checkoutTime,
        reservation?.startDate,
        reservation?.endDate,
    ]);

    return (
        <Accordion
            expanded={expanded}
            onChange={onToggle}
            disableGutters
            square={false}
            sx={outerAccordionSx}
        >
            <AccordionSummary
                aria-controls='reservation-itinerary-content'
                id='reservation-itinerary-header'
                sx={{
                    py: 1.25,
                    backgroundColor: theme.palette.neutral.main,
                    px: 0,
                }}
            >
                <Typography
                    component='h2'
                    variant='body1'
                    sx={{ fontWeight: 700, letterSpacing: 0.2 }}
                >
                    Reservation Itinerary
                </Typography>
            </AccordionSummary>

            <AccordionDetails
                sx={{
                    px: 0,
                    pb: isMobile ? 2 : 3,
                    pt: 0,
                    backgroundColor: theme.palette.neutral.main,
                }}
            >
                <Divider sx={{ mt: 0, mb: 2 }} />

                <Box sx={cardSx}>
                    <SpaceItinerary spaces={spaces} />
                </Box>
            </AccordionDetails>
        </Accordion>
    );
}
